import styled from "@emotion/styled";
import { Typography } from "@mui/material";
import { BlDropdown as DropdownInput } from "Components/_Shared/Inputs/BlDropdown";
import { StyledFormControl, StyledValidationText } from "Forms/Components/FormStyles";
import type { CodeListItem } from "Infrastructure/Models/ICodeListDto";
import { Spacing } from "Infrastructure/Themes/Themes";
import type { JSX } from "react";
import { type Control, Controller, type DeepMap, type FieldError, type FieldPath, type FieldValues } from "react-hook-form";

const StyledLabel = styled.div`
  display: flex;
  color: ${(props) => props.theme.palette.text.primary};
  margin-bottom:  ${(props) => props.theme.spacing(Spacing.S)};
`;

export type DropdownValueType = string | number | boolean;

type Props<FormType extends FieldValues, U extends boolean | undefined> = (U extends true
  ? {
      onChange?: (value: DropdownValueType[] | null) => void;
    }
  : {
      onChange?: (value: DropdownValueType | null) => void;
    }) & {
  codeList: CodeListItem[];
  name: FieldPath<FormType>;
  formControlClass?: "long" | "medium" | "tiny" | string;
  label?: string;
  control: Control<FormType, object>;
  errors: DeepMap<any, FieldError>;
  disabled?: boolean;
  placeholder?: string;
  hasFilterStyle?: boolean;
  multiple?: boolean;
  labelEndAdornment?: JSX.Element;
  hasValidationText?: boolean;
};

export const BlDropdown = <T extends object, U extends boolean>(props: Props<T, U>) => {
  const {
    codeList,
    name,
    control,
    label,
    disabled,
    errors,
    formControlClass,
    placeholder,
    hasFilterStyle,
    multiple,
    labelEndAdornment,
    onChange,
    hasValidationText = true,
  } = props;
  const error: FieldError | undefined = errors[name];
  return (
    <>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange: controllerOnChange, value, onBlur, name } }) => {
          const isFilled =
            !!value &&
            (multiple
              ? codeList.some((e) => (value as []).some((v) => e.id === v || e.code === v))
              : codeList.some((e) => e.id === value || e.code === value));

          const formValue = isFilled ? value : multiple ? [] : "";

          const onValueChange = (val: DropdownValueType | DropdownValueType[] | null) => {
            controllerOnChange(val);
            if (onChange) {
              onChange(val as any);
            }
          };

          return (
            <>
              <StyledFormControl
                className={`bl-form-control ${formControlClass ?? ""} ${!label ? "no-label" : ""} ${hasFilterStyle ? "filter" : ""}`}
              >
                {label && (
                  <StyledLabel>
                    <Typography fontWeight={500} variant="body2" mr={Spacing.S}>
                      {label}
                    </Typography>
                    {labelEndAdornment}
                  </StyledLabel>
                )}
                <DropdownInput
                  value={formValue}
                  onChange={onValueChange}
                  onBlur={onBlur}
                  disabled={disabled}
                  hasError={!!error}
                  name={name}
                  placeholder={placeholder}
                  options={codeList.map((x) => ({
                    Name: x.name,
                    Value: x.id ?? x.code,
                  }))}
                  hasFilterStyle={hasFilterStyle}
                  multiple={multiple}
                />
                {hasValidationText && (
                  <StyledValidationText $hasFilterStyle={hasFilterStyle}>{!!error && error.message}</StyledValidationText>
                )}
              </StyledFormControl>
            </>
          );
        }}
      />
    </>
  );
};
